import { useEffect } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { TopBar } from './TopBar';
import { BottomTabBar } from './BottomTabBar';
import { ToastViewport } from '@/components/Toast';
import { bindBackButton } from '@/lib/tg';
import { useTextInputFocused } from './useTextInputFocused';

// Telegram WebApp events that should trigger a data refresh. `activated` fires
// when the Mini App gains focus; `visibilityChange` is a generic focus signal.
const WAKE_EVENTS = ['activated', 'visibilityChanged'] as const;

interface TgWebApp {
  onEvent?: (name: string, cb: () => void) => void;
  offEvent?: (name: string, cb: () => void) => void;
  isVisible?: boolean;
}

export function AppShell() {
  const location = useLocation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  // While typing, hide the fixed tab bar so it doesn't cover the input.
  const typing = useTextInputFocused();

  useEffect(() => {
    const isRoot = location.pathname === '/';
    if (isRoot) {
      bindBackButton(null);
      return;
    }
    const handler = () => navigate(-1);
    bindBackButton(handler);
    return () => bindBackButton(null);
  }, [location.pathname, navigate]);

  // Refetch on Mini App focus — the TG WebView suspends JS timers while
  // backgrounded, so polling alone isn't enough when the user returns.
  useEffect(() => {
    const tg = (
      globalThis as unknown as { Telegram?: { WebApp?: TgWebApp } }
    ).Telegram?.WebApp;
    if (!tg?.onEvent || !tg.offEvent) return;

    const handler = (): void => {
      void qc.invalidateQueries();
    };
    WAKE_EVENTS.forEach((e) => tg.onEvent?.(e, handler));
    return () => {
      WAKE_EVENTS.forEach((e) => tg.offEvent?.(e, handler));
    };
  }, [qc]);

  return (
    <div className="min-h-full flex flex-col bg-bg text-text">
      <TopBar />
      <main className="flex-1 px-4 pb-24 pt-3 max-w-screen-md w-full mx-auto">
        <Outlet />
      </main>
      {!typing && <BottomTabBar />}
      <ToastViewport />
    </div>
  );
}
